import React, { useState, useEffect, useCallback } from 'react';
import CardFilters from '@components/CardFilters/CardFilters';
import { withStyles, MuiThemeProvider, createMuiTheme } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import formatMessage from './i18n/formatMessage';
import { deleteCertificacaoById, findAllCertificacoes } from '@resources/Certificacao';
import DataTable from '@components/DataTable/DataTable';
import GroupButtonGrid from '@components/GroupButtonGrid/GroupButtonGrid';
import Loading from '@components/Loading/Loading';
import swal from '@sweetalert/with-react';
import ButtonFAB from '@components/ButtonFAB/ButtonFAB';
import iconAdd from '@images/icone_add.png';
import FilterUtil from '../../../filterUtil';

const style = theme => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    overflowY: 'scroll',
    paddingBottom: 15
  },
  cardGrid: {
    marginTop: 30,
    marginBottom: 15,
    display: 'contents',
    width: '100%',
    height: '100%'
  }
});

const theme = createMuiTheme({
  overrides: {
    MuiInput: {
      formControl: {
        marginTop: '15px !important'
      },
      input: {
        fontSize: 14,
        fontFamily: "'Roboto', sans-serif",
        width: '100%',
        height: 14,
        whiteSpace: 'noWrap',
        textOverflow: 'ellipsis'
      },
      underline: {
        '&:before': {
          borderBottom: '1px solid #000000',
          opacity: 0.35
        }
      }
    },
    MuiFormControl: {
      root: {
        marginLeft: '0px !important'
      }
    },
    MuiButton: {
      contained: {
        height: '46px',
        width: '100px',
        marginLeft: '15px'
      },
      containedPrimary: {
        backgroundColor: '#F33A30',
        '&:hover': {
          backgroundColor: '#F33A30',
          '@media (hover: none)': {
            backgroundColor: '#F33A30'
          }
        }
      },
      containedSecondary: {
        backgroundColor: '#00622B',
        '&:hover': {
          backgroundColor: '#00622B',
          '@media (hover: none)': {
            backgroundColor: '#00622B'
          }
        }
      }
    }
  },
  palette: {
    primary: {
      light: '#00622B',
      main: '#00622B',
      dark: '#00622B',
      contrastText: '#fff'
    }
  },
  typography: {
    useNextVariants: true
  }
});

const headDatatable = [
  {
    field: 'nmTipoEcosocial',
    headerName: formatMessage('certificacao.descricao'),
    col: 6,
    sort: 'asc'
  }
];

const filtersConfig = [
  {
    label: formatMessage('certificacao.descricao'),
    field: 'nmTipoEcosocial',
    type: 'string'
  }
];

/**
 * Screen de listagem de certificações
 *
 * @author Bruno Eduardo <bruno.soares@kepha.com.br>
 * @param {*} props
 * @returns {JSX.Element}
 */
function Certificacoes(props) {
  const { classes } = props;

  const [dataInTable, setDataInTable] = useState([]);
  const [dataAppSync, setDataAppSync] = useState([]);
  const [disabledButtons, setDisabledButtons] = useState(true);
  const [itemSelect, setItemSelect] = useState(null);
  const [isLoading, setLoading] = useState(true);

  const paginacao = { length: dataInTable ? dataInTable.length : 0, page: 0 };

  const onFilter = useCallback((dataChips = []) => {
    setLoading(true);
    findAllCertificacoes()
      .then(res => {
        setDataInTable(FilterUtil.applyFilters(res.data, dataChips));
        setDataAppSync(res.data);
      })
      .catch(err => {
        setDataInTable([]);
        setDataAppSync([]);
        swal(formatMessage('certificacao.falhaoAoBuscarDados'), {
          icon: 'error',
          buttons: {
            confirm: {
              text: 'Ok',
              value: true,
              visible: true,
              closeModal: true,
              className: 'swal2-error'
            }
          }
        });
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(
    () => {
      const title = 'certificacao.certificacoes';
      if (props.itensState.name !== title) {
        props.dispatch({ type: 'UPDATE_TOOLBAR', toolbar: formatMessage(title), name: title });
      }

      onFilter();
    },
    [onFilter]
  );

  return (
    <MuiThemeProvider theme={theme}>
      <div className={classes.card}>
        {isLoading && <Loading />}

        <CardFilters
          filtros={filtersConfig}
          usePaddings={false}
          onFilter={onFilter}
          onFilterChanged={onFilterChanged}
          viewKey='certificacao'
        />

        <GroupButtonGrid
          withoutMargin
          showAdd={false}
          showEdit
          showDelete
          disableEdit={disabledButtons}
          disableDelete={disabledButtons}
          onClickEdit={handleEdit}
          onClickDelete={handleDelete}
        />

        <div className={classes.cardGrid}>
          <DataTable
            noMargin
            borderRadius={false}
            data={dataInTable}
            columns={headDatatable}
            selectChange={handleChangeSelected}
            showPagination
            pagination={paginacao}
          />
        </div>

        <ButtonFAB
          icon={iconAdd}
          positionDefault
          onClick={() => props.history.push(`/app/certificacao-ecosocial/novo`)}
          disableFAB={isLoading}
        />
      </div>
    </MuiThemeProvider>
  );

  /**
   * Filtra os dados que aparecem no DataTable
   *
   * @param {*} dataChips - Lista de filtros
   */
  function onFilterChanged(dataChips) {
    setDataInTable(FilterUtil.applyFilters(dataAppSync, dataChips));
  }

  /**
   * Manipula o evento que é disparado quando uma linha do DataTable é selecionada
   *
   * @param {*} select - Objeto selecionado
   */
  function handleChangeSelected(select) {
    if (select) {
      setItemSelect(select);
      setDisabledButtons(false);
    } else {
      setItemSelect(null);
      setDisabledButtons(true);
    }
  }

  /**
   * Manipula o evento que é disparado quando é selecionado um item para editar
   */
  function handleEdit() {
    if (itemSelect) {
      props.history.push(`/app/certificacao-ecosocial/editar/${itemSelect.idTipoEcosocial}`);
    }
  }

  /**
   * Manipula o evento que é disparado quando um item está sendo excluído
   */
  function handleDelete() {
    if (!itemSelect) return;

    swal({
      title: formatMessage('certificacao.desejaExcluirCertificacao'),
      icon: 'warning',
      closeOnClickOutside: false,
      closeOnEsc: true,
      buttons: {
        cancel: {
          text: formatMessage('certificacao.nao'),
          value: false,
          visible: true,
          closeModal: true,
          className: 'swal2-cancel'
        },
        confirm: {
          text: formatMessage('certificacao.excluir'),
          value: true,
          visible: true,
          closeModal: true,
          className: 'swal2-confirm'
        }
      }
    }).then(res => {
      if (!res) {
        return;
      }
      setLoading(true);

      deleteCertificacaoById(itemSelect.idTipoEcosocial)
        .then(res => {
          swal(formatMessage('certificacao.certificacaoExcluida'), {
            icon: 'success',
            buttons: {
              confirm: {
                text: 'Ok',
                value: true,
                visible: true,
                closeModal: true,
                className: 'swal2-Ok'
              }
            }
          });

          onFilter(props.chipData);
        })
        .catch(err => {
          setLoading(false);
          if (err.response !== undefined && err.response.data.codigo === 'ENTIDADE_RELACIONADA') {
            swal(formatMessage('certificacao.produtorRelacionado'), {
              icon: 'error',
              buttons: {
                confirm: {
                  text: 'Ok',
                  value: true,
                  visible: true,
                  closeModal: true,
                  className: 'swal2-error'
                }
              }
            });
            return;
          }
          swal(formatMessage('certificacao.falhaoAoExcluir'), {
            icon: 'error',
            buttons: {
              confirm: {
                text: 'Ok',
                value: true,
                visible: true,
                closeModal: true,
                className: 'swal2-error'
              }
            }
          });
        });
    });
  }
}

/**
 * Mapeia as props da store para o componente
 *
 * @param {*} state - State da store
 */
const mapStateToProps = state => ({
  itensState: state.toolbar,
  infoUsuario: state.adicionarInfoUsuario.info,
  chipData: state.chips.chipData.filter(date => {
    return date.id === 'certificacao';
  })[0].content
});

export default withRouter(connect(mapStateToProps)(withStyles(style)(Certificacoes)));
